import { FieldSchema, FieldType } from '@vendasta/crm';
import {
  chooseHandler,
  FieldHandler,
  FieldHandlerValidator,
  IntegerFieldHandler,
} from '@vendasta/auxiliary-data-components';
import { Validators } from '@angular/forms';
import { TemplateableFieldHandler } from '../handlers/templateable-field-handler';
import { FieldTypeString } from './interface';
import { TagsHandler } from '../handlers/tags';

function convertFieldTypeToString(fieldType: FieldType): FieldTypeString {
  switch (fieldType) {
    case FieldType.FIELD_TYPE_CURRENCY:
      return 'currency';
    case FieldType.FIELD_TYPE_DATE:
      return 'date';
    case FieldType.FIELD_TYPE_STRING:
      return 'string';
    case FieldType.FIELD_TYPE_DROPDOWN:
      return 'dropdown';
    case FieldType.FIELD_TYPE_INTEGER:
      return 'integer';
    case FieldType.FIELD_TYPE_EMAIL:
      return 'email';
    case FieldType.FIELD_TYPE_PHONE:
      return 'phone';
    case FieldType.FIELD_TYPE_BOOLEAN:
      return 'boolean';
    case FieldType.FIELD_TYPE_TAG:
      return 'tag';
    case FieldType.FIELD_TYPE_DATETIME:
      return 'datetime';
    case FieldType.FIELD_TYPE_STRING_LIST:
      return 'stringList';
    case FieldType.FIELD_TYPE_GEOPOINT:
      return 'geopoint';
    case FieldType.FIELD_TYPE_FLOAT:
      return 'float';
    default:
      return '';
  }
}

function getValidatorsForType(fieldType: FieldType, selectedOperation: string): FieldHandlerValidator[] {
  const requiredValidator = {
    errorCode: 'required',
    errorMessage: 'AUTOMATIONS.EDITOR.ERRORS.REQUIRED',
    validatorFn: Validators.required,
  };
  const divisionByZeroValidator = {
    errorCode: 'divisionByZero',
    errorMessage: 'AUTOMATIONS.EDITOR.TASKS.MODIFY_CUSTOM_FIELD_DATA.DIVIDE_BY_ZERO_ERROR',
    validatorFn: (control: { value: number }) => (control.value === 0 ? { divisionByZero: true } : null),
  };
  const currencyRequiredValidator: FieldHandlerValidator = {
    errorCode: 'currencyRequired',
    errorMessage: 'AUTOMATIONS.EDITOR.ERRORS.REQUIRED',
    validatorFn: (control) => {
      if (control.value === '' || control.value === null || isNaN(control.value)) {
        return { currencyRequired: true };
      }
      return null;
    },
  };

  switch (fieldType) {
    case FieldType.FIELD_TYPE_CURRENCY: {
      const currencyValidators = [currencyRequiredValidator];
      if (selectedOperation === 'DIVIDE') {
        currencyValidators.push(divisionByZeroValidator);
      }
      return currencyValidators;
    }
    case FieldType.FIELD_TYPE_DATE:
      return [requiredValidator];
    case FieldType.FIELD_TYPE_STRING:
      return [requiredValidator];
    case FieldType.FIELD_TYPE_DROPDOWN:
      return [requiredValidator];
    case FieldType.FIELD_TYPE_INTEGER: {
      const integerValidators = [requiredValidator];
      if (selectedOperation === 'DIVIDE') {
        integerValidators.push(divisionByZeroValidator);
      }
      return integerValidators;
    }
    // TODO revisit these validators
    case FieldType.FIELD_TYPE_EMAIL:
      return [requiredValidator];
    case FieldType.FIELD_TYPE_PHONE:
      return [requiredValidator];
    case FieldType.FIELD_TYPE_BOOLEAN:
      return [requiredValidator];
    case FieldType.FIELD_TYPE_TAG:
      return [requiredValidator];
    case FieldType.FIELD_TYPE_DATETIME:
      return [requiredValidator];
    case FieldType.FIELD_TYPE_STRING_LIST:
      return [requiredValidator];
    case FieldType.FIELD_TYPE_GEOPOINT:
      return [requiredValidator];
    case FieldType.FIELD_TYPE_FLOAT:
      return [requiredValidator];
    default:
      return [];
  }
}

export function chooseFieldHandler(
  selectedFieldSchema: FieldSchema,
  selectedOperation: string,
  fieldValue: any,
  crmObjectType?: string,
): FieldHandler {
  if (
    selectedFieldSchema?.fieldType === FieldType.FIELD_TYPE_CURRENCY &&
    ['DIVIDE', 'MULTIPLY'].includes(selectedOperation)
  ) {
    return new IntegerFieldHandler(
      selectedFieldSchema.fieldId,
      selectedFieldSchema.fieldName,
      '',
      fieldValue,
      selectedFieldSchema.fieldDescription,
      getValidatorsForType(selectedFieldSchema.fieldType, selectedOperation),
    );
  } else if (selectedFieldSchema?.fieldType === FieldType.FIELD_TYPE_STRING && selectedOperation !== 'CLEAR_VALUE') {
    return new TemplateableFieldHandler(
      selectedFieldSchema.fieldId,
      selectedFieldSchema.fieldName,
      '',
      fieldValue,
      selectedFieldSchema.fieldDescription,
      getValidatorsForType(selectedFieldSchema.fieldType, selectedOperation),
    );
  }

  if (selectedFieldSchema?.fieldType === FieldType.FIELD_TYPE_TAG) {
    return new TagsHandler(
      selectedFieldSchema.fieldId,
      selectedFieldSchema.fieldName,
      '',
      fieldValue,
      selectedFieldSchema.fieldDescription,
      getValidatorsForType(selectedFieldSchema.fieldType, selectedOperation),
      false,
      crmObjectType ?? '',
    );
  }

  return chooseHandler(
    selectedFieldSchema.fieldId,
    {
      description: selectedFieldSchema.fieldDescription,
      title: selectedFieldSchema.fieldName,
      type: convertFieldTypeToString(selectedFieldSchema.fieldType),
      dropdownOptions: selectedFieldSchema.dropdownOptions?.map((opt) => ({
        label: opt?.label || '',
        value: opt?.value || '',
      })),
      currencyCode: selectedFieldSchema.currencyCode,
      format: '',
    },
    fieldValue,
    false,
    getValidatorsForType(selectedFieldSchema.fieldType, selectedOperation),
  );
}
